import { LANDMASSES, WORLD, clamp, terrainHeight } from "@tiny-seas/shared";

/**
 * CPU half of the shore atlas (`shore.ts` uploads it): terrain height and local
 * slope around each landmass, one square tile per landmass. No Babylon imports,
 * so the bake worker can run it off the main thread.
 */
export const SHORE = { tile: 512, min: WORLD.seaFloor, range: 2 - WORLD.seaFloor, extent: 2 } as const;
/** Tiles per atlas side. */
export const SHORE_TILES = Math.ceil(Math.sqrt(LANDMASSES.length));
/** Atlas side in texels. */
export const SHORE_SIZE = SHORE.tile * SHORE_TILES;

/** Per landmass: centre x, centre z, half extent, unused. */
export function shoreRects(): number[] {
  return LANDMASSES.flatMap((l) => [l.cx, l.cz, l.radius * SHORE.extent, 0]);
}

/**
 * RGBA texels of rows [y0, y1) of landmass `index`'s tile. RG packs height,
 * B the slope from central differences one texel apart. Heights are sampled once
 * per texel centre on a one-texel apron: the neighbours of a texel are exactly the
 * samples the central differences need, so each height is evaluated once, not five times.
 */
export function bakeShoreRows(index: number, y0: number, y1: number): Uint8Array {
  const l = LANDMASSES[index]!;
  const { tile, min, range, extent } = SHORE;
  const e = l.radius * extent;
  const spacing = 2 * e / tile;
  const stride = tile + 2;
  // heights[(row - y0 + 1) * stride + col + 1] for rows y0-1..y1 and columns -1..tile.
  const heights = new Float64Array((y1 - y0 + 2) * stride);
  for (let ty = y0 - 1; ty <= y1; ty++) {
    const z = l.cz + ((ty + 0.5) / tile - 0.5) * 2 * e;
    let h = (ty - y0 + 1) * stride;
    for (let tx = -1; tx <= tile; tx++, h++) heights[h] = terrainHeight(l.cx + ((tx + 0.5) / tile - 0.5) * 2 * e, z);
  }
  const out = new Uint8Array((y1 - y0) * tile * 4);
  for (let ty = y0, o = 0; ty < y1; ty++) {
    let h = (ty - y0 + 1) * stride + 1;
    for (let tx = 0; tx < tile; tx++, h++, o += 4) {
      const v = Math.round(clamp((heights[h]! - min) / range, 0, 1) * 65535);
      const dx = (heights[h + 1]! - heights[h - 1]!) / (2 * spacing);
      const dz = (heights[h + stride]! - heights[h - stride]!) / (2 * spacing);
      out[o] = v >> 8;
      out[o + 1] = v & 255;
      out[o + 2] = Math.round(clamp(Math.hypot(dx, dz) / 2, 0, 1) * 255);
      out[o + 3] = 255;
    }
  }
  return out;
}

/** Copies rows from `bakeShoreRows(index, y0, …)` into their place in the atlas. */
export function placeShoreRows(atlas: Uint8Array, index: number, y0: number, rows: Uint8Array): void {
  const { tile } = SHORE;
  const tileX = index % SHORE_TILES;
  const tileY = Math.floor(index / SHORE_TILES);
  const row = tile * 4;
  for (let r = 0; r < rows.length / row; r++) {
    atlas.set(rows.subarray(r * row, (r + 1) * row), ((tileY * tile + y0 + r) * SHORE_SIZE + tileX * tile) * 4);
  }
}

/** The whole atlas on the calling thread. */
export function bakeShoreAtlas(): Uint8Array {
  const atlas = new Uint8Array(SHORE_SIZE * SHORE_SIZE * 4);
  for (let i = 0; i < LANDMASSES.length; i++) placeShoreRows(atlas, i, 0, bakeShoreRows(i, 0, SHORE.tile));
  return atlas;
}
